// File: services/api.js
// Purpose: Thin client wrappers around backend endpoints used by the frontend.
// Notes: Centralizes API base URL and error handling so views can call simple helpers.
import axios from "axios";

// Determine API base URL from build-time env `VITE_BACKEND_URL` (set on Netlify)
// Parcel inlines `process.env.*` during build, so use that to avoid parser issues.
// Fallback to localhost for local development.
const API_BASE = process.env.VITE_BACKEND_URL || "http://localhost:3000";

// Game cover art lives in Assets/game-covers and is served by the API's
// express.static of the repo root, so it works locally and on Render without
// needing the bundler to fingerprint 50 images.
export function gameCoverUrl(file) {
  return file ? `${API_BASE}/Assets/game-covers/${file}` : null;
}

// Any other static asset served out of the repo root (console logos, etc).
export function assetUrl(path) {
  return path ? `${API_BASE}/Assets/${path}` : null;
}

export async function fetchHomeData() {
  // allSettled, not all: these are three independent TMDB calls. With
  // Promise.all, one of them failing (e.g. now_playing 500s while trending
  // and popular both succeed) rejected the whole thing and the catch below
  // wiped all three to [] — the home page went fully blank over one bad
  // call instead of just losing the row that actually failed.
  const [trending, nowPlaying, popular] = await Promise.allSettled([
    axios.get(`${API_BASE}/movies/trending`),
    axios.get(`${API_BASE}/movies/now_playing`),
    axios.get(`${API_BASE}/movies/popular`)
  ]);

  const results = r => (r.status === "fulfilled" ? r.value.data.results : []);
  [trending, nowPlaying, popular].forEach((r, i) => {
    if (r.status === "rejected") {
      console.error(
        `HOME DATA ERR (${["trending", "now_playing", "popular"][i]}):`,
        r.reason
      );
    }
  });

  return {
    trending: results(trending),
    nowPlaying: results(nowPlaying),
    popular: results(popular)
  };
}

// popular
export async function fetchPopular() {
  try {
    const response = await axios.get(`${API_BASE}/movies/popular`);
    return response.data.results;
  } catch (err) {
    console.error("POPULAR MOVIES ERROR:", err);
    return [];
  }
}

// upcoming
export async function fetchUpcoming() {
  try {
    const response = await axios.get(`${API_BASE}/movies/now_playing`);
    return response.data.results;
  } catch (err) {
    console.error("UPCOMING MOVIES ERROR:", err);
    return [];
  }
}

// now playing
export async function fetchNowPlaying() {
  try {
    const response = await axios.get(`${API_BASE}/movies/now_playing`);
    return response.data.results;
  } catch (err) {
    console.error("NOW PLAYING ERROR:", err);
    return [];
  }
}

// box office attempt. still not working
export async function fetchBoxOffice() {
  try {
    const res = await axios.get(`${API_BASE}/boxoffice`);
    return res.data.results;
  } catch (err) {
    console.error("BOX OFFICE ERROR:", err);
    return [];
  }
}

// trailer videos
export async function fetchMovieVideos(movieId) {
  try {
    const response = await axios.get(`${API_BASE}/movies/${movieId}/videos`);
    return response.data.results;
  } catch (err) {
    console.error("VIDEO API ERROR:", err);
    return [];
  }
}

// Top-rated movies on TMDB (complements Trending / Now Playing / Popular).
export async function fetchTopRated() {
  try {
    const response = await axios.get(`${API_BASE}/movies/top_rated`);
    return response.data.results || [];
  } catch (err) {
    console.error("TOP RATED ERROR:", err);
    return [];
  }
}

// What's playing in theaters near a US ZIP, with pre-filled ticket links.
export async function fetchInTheaters(zip) {
  try {
    const response = await axios.get(`${API_BASE}/movies/in-theaters`, { params: { zip } });
    return response.data;
  } catch (err) {
    const message = err.response?.data?.message || "Could not load showtimes";
    console.error("IN THEATERS ERROR:", err);
    return { location: null, results: [], error: message };
  }
}

// TV shows — popular / top rated / on the air, plus the provider list.
export async function fetchTvShows() {
  try {
    const response = await axios.get(`${API_BASE}/tv`);
    return response.data;
  } catch (err) {
    console.error("TV ERROR:", err);
    return { popular: [], topRated: [], onTheAir: [], providers: [] };
  }
}

// Shows available on one streaming service (TMDB provider id).
export async function fetchTvByProvider(provider) {
  try {
    const response = await axios.get(`${API_BASE}/tv/providers`, { params: { provider } });
    return response.data;
  } catch (err) {
    console.error("TV PROVIDER ERROR:", err);
    return { results: [], total_results: 0 };
  }
}

// Nearest cinemas — separate call because the OpenStreetMap lookup behind it
// can take ~20s cold, and the movie grid shouldn't wait on it.
export async function fetchTheatersNear(zip) {
  try {
    const response = await axios.get(`${API_BASE}/movies/theaters-near`, { params: { zip } });
    return response.data;
  } catch (err) {
    console.error("THEATERS NEAR ERROR:", err);
    return { theaters: [], radius: 25 };
  }
}

// Full movie detail — runtime, genres, cast, similar, where to watch.
export async function fetchMovieDetails(id) {
  try {
    const response = await axios.get(`${API_BASE}/movies/${id}/details`);
    return response.data;
  } catch (err) {
    console.error("MOVIE DETAIL ERROR:", err);
    return null;
  }
}

// Genres — TMDB's movie genre list (used by the Discover page).
export async function fetchGenres() {
  try {
    const response = await axios.get(`${API_BASE}/movies/genres`);
    return Array.isArray(response.data?.genres) ? response.data.genres : [];
  } catch (err) {
    console.error("GENRES ERROR:", err);
    return [];
  }
}

// Discover — filterable browse. Accepts { genres, year, minRating, sort, page }.
export async function discoverMovies(filters = {}) {
  const params = {};
  if (filters.genres && filters.genres.length) params.genres = filters.genres.join(",");
  if (filters.year) params.year = filters.year;
  if (filters.minRating) params.minRating = filters.minRating;
  if (filters.sort) params.sort = filters.sort;
  if (filters.page) params.page = filters.page;
  try {
    const response = await axios.get(`${API_BASE}/movies/discover`, { params });
    return response.data || { results: [], page: 1, total_pages: 0 };
  } catch (err) {
    console.error("DISCOVER ERROR:", err);
    return { results: [], page: 1, total_pages: 0 };
  }
}

// Person detail + filmography (cast credits sorted by popularity).
export async function fetchPersonDetails(id) {
  try {
    const response = await axios.get(`${API_BASE}/person/${id}/details`);
    return response.data;
  } catch (err) {
    console.error("PERSON ERROR:", err);
    return null;
  }
}

// Movie search — proxied through our backend (TMDB /search/movie).
export async function searchMovies(query) {
  const q = String(query || "").trim();
  if (q.length < 2) return [];
  try {
    const response = await axios.get(`${API_BASE}/movies/search`, {
      params: { q }
    });
    return Array.isArray(response.data?.results) ? response.data.results : [];
  } catch (err) {
    console.error("SEARCH ERROR:", err);
    return [];
  }
}

// Movie news — proxied through our backend (Guardian Film section).
export async function fetchMovieNews() {
  try {
    const response = await axios.get(`${API_BASE}/news`);
    return Array.isArray(response.data?.results) ? response.data.results : [];
  } catch (err) {
    console.error("MOVIE NEWS ERROR:", err);
    return [];
  }
}

// TV news — proxied through our backend (TV-focused RSS + Guardian TV).
export async function fetchTvNews() {
  return fetchNewsTab("tv");
}

// Streaming and gaming wires — same aggregator, different source lists.
export async function fetchStreamingNews() {
  return fetchNewsTab("streaming");
}

export async function fetchGamingNews() {
  return fetchNewsTab("gaming");
}

// Shared fetcher for every non-movie news tab. `movies` lives at /news.
// Rethrows on failure rather than swallowing to `[]` — a genuine outage and
// "nothing matched" were otherwise indistinguishable to the caller, which is
// what left the news page stuck on "Loading the wire…" forever during a
// real outage instead of showing an error.
export async function fetchNewsTab(tab) {
  const path = tab === "movies" ? "/news" : `/news/${tab}`;
  try {
    const response = await axios.get(`${API_BASE}${path}`);
    return Array.isArray(response.data?.results) ? response.data.results : [];
  } catch (err) {
    console.error(`${String(tab).toUpperCase()} NEWS ERROR:`, err);
    throw err;
  }
}

// Comments API helpers. Route returns an array of comment docs directly.
export async function fetchComments(movieId) {
  try {
    const response = await axios.get(`${API_BASE}/comments/${movieId}`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (err) {
    console.error("FETCH COMMENTS ERROR:", err);
    return [];
  }
}

// Posting/deleting now require login — the server trusts the token for who
// you are, not anything the client sends, so the Authorization header is
// what actually lets these through.
function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function postComment(commentData) {
  try {
    const response = await axios.post(`${API_BASE}/comments`, commentData, {
      headers: authHeaders()
    });
    return response.data;
  } catch (err) {
    console.error("POST COMMENT ERROR:", err);
    throw err;
  }
}

export async function deleteComment(id) {
  try {
    await axios.delete(`${API_BASE}/comments/${id}`, { headers: authHeaders() });
    return true;
  } catch (err) {
    console.error("DELETE COMMENT ERROR:", err);
    throw err;
  }
}

// curated upcoming movies
export async function fetchUpcomingCurated() {
  try {
    const response = await axios.get(`${API_BASE}/movies/upcoming-curated`);
    return response.data; // { "2026": [...], "2027": [...], popular: [...] }
  } catch (err) {
    console.error("CURATED UPCOMING ERROR:", err);
    return { "2026": [], "2027": [], popular: [] };
  }
}

// Avengers: Doomsday — detail, full billed cast and the newest official
// trailer, for the Marvel page's hero hub.
export async function fetchDoomsday() {
  try {
    const response = await axios.get(`${API_BASE}/movies/doomsday`);
    return response.data || null;
  } catch (err) {
    console.error("DOOMSDAY API ERROR:", err);
    return null;
  }
}

export async function fetchMarvelMovies() {
  try {
    const response = await axios.get(`${API_BASE}/movies/marvel`);
    return response.data.movies;
  } catch (err) {
    console.error("MARVEL API ERROR:", err);
    return [];
  }
}
